<script setup lang="ts">
import { CoreEventSource } from '@apps/main/new-desktop/core/models';
import { HotelFiltersButtons } from '@apps/main/new-desktop/modules/hotelFilters';
import SemboLogo from '@apps/main/new-desktop/core/components/SemboLogo.vue';

import { AppBackButton } from '@libs/ui';

import FirstDayTripPriceRibbonMobile from '../../../core/components/FirstDayTripPriceRibbonMobile.vue';
import {
  useHotelSelectionModalsStore,
  useHotelSelectionStore,
} from '../stores';

import HotelSelectionDestination from './HotelSelectionDestination.vue';

interface HotelSelectionHeaderMobileProps {
  backButtonLink: string;
  shouldShowBackButton?: boolean;
}
const props = defineProps<HotelSelectionHeaderMobileProps>();

interface HotelSelectionHeaderMobileEmits {
  (event: 'back'): void;
}
const emit = defineEmits<HotelSelectionHeaderMobileEmits>();

const hotelSelectionStore = useHotelSelectionStore();
const hotelSelectionModalsStore = useHotelSelectionModalsStore();
</script>

<template>
  <div class="hotel-selection-header-mobile-actions">
    <AppBackButton
      v-if="props.shouldShowBackButton"
      type="black"
      :text="$t('planner.back')"
      :link="props.backButtonLink"
      font="font-label-m-light"
      @click="emit('back')"
    />
    <SemboLogo v-else class="logo" color="color-sembo-purple" />
  </div>
  <div class="hotel-selection-header-mobile">
    <FirstDayTripPriceRibbonMobile
      show-price-ribbon
      shadow
      :show-month-select-dropdown="false"
      :source="CoreEventSource.HotelSelection"
    >
      <template #bottom-row>
        <div class="bottom-row">
          <div class="actions-section">
            <HotelSelectionDestination
              v-if="hotelSelectionStore.hotelSelectionHeaderData"
              class="hotel-selection-destination"
              :data="hotelSelectionStore.hotelSelectionHeaderData"
              @open-filters="hotelSelectionStore.openFilters('list')"
              @change-number-of-nights="
                hotelSelectionStore.changeNumberOfNights
              "
              @change-destination="
                hotelSelectionModalsStore.openDestinationSelectorModal(
                  $event,
                  hotelSelectionStore.editingIngredientId
                )
              "
            />
          </div>
        </div>
      </template>
    </FirstDayTripPriceRibbonMobile>
  </div>
  <HotelFiltersButtons
    class="hotel-selection-header-mobile-filters"
    :suggestion-type="hotelSelectionStore.listSuggestionType"
    :board-basis-options="hotelSelectionStore.hotelMealFilterOptions"
    :amenities-options="hotelSelectionStore.hotelAmenitiesFilterOptions"
    :accommodation-type-options="
      hotelSelectionStore.accommodationTypeFilterOptions
    "
    :neighborhood-polygons-options="
      hotelSelectionStore.neighborhoodPolygonsFilterOptions
    "
    :ingredient="hotelSelectionStore.editingStayIngredient"
    @open-all-filters="hotelSelectionStore.openFilters('list')"
    @open-why-we-suggest-modal="hotelSelectionModalsStore.openWhyWeSuggestModal"
    @suggestion-type-selected="hotelSelectionStore.setListSuggestionType"
  />
</template>

<style scoped lang="scss">
.hotel-selection-header-mobile-actions {
  position: sticky;
  top: 0;
  z-index: 3;

  padding: var(--spacing-12);
  background-color: var(--color-white);
}

.hotel-selection-header-mobile {
  z-index: 2;

  .show-price-ribbon-button {
    color: var(--color-neutral-70);
  }

  .bottom-row {
    .actions-section {
      background-color: var(--color-white);
      padding: var(--spacing-12) 0 var(--spacing-8) var(--spacing-16);
      gap: var(--spacing-12);
      display: flex;
      flex-direction: column;
      justify-content: center;
      .hotel-selection-destination {
        padding-right: var(--spacing-16);
      }
    }
  }
}

.hotel-selection-header-mobile-filters {
  position: sticky;
  top: var(--spacing-48);
  z-index: 3;

  padding: 0 0 var(--spacing-12) var(--spacing-16);
  background-color: var(--color-white);
  box-shadow: 0px 3px 4px -1px #202f4714;
}
</style>
